import React, { useEffect, useRef } from 'react';
import { AD_CONFIG, shouldShowAds, getAdSlot } from '../config/ads';
import { shouldExcludeAds } from './AdExclusionManager';

interface AdComponentProps {
  adSlot: string;
  adFormat?: 'auto' | 'rectangle' | 'vertical' | 'horizontal';
  adStyle?: React.CSSProperties;
  className?: string;
}

export const AdComponent: React.FC<AdComponentProps> = ({ 
  adSlot, 
  adFormat = 'auto', 
  adStyle = { display: 'block' },
  className = ''
}) => {
  const adRef = useRef<HTMLModElement>(null);

  useEffect(() => {
    // Only load ads if the component is mounted and we're in the blog section
    if (!adRef.current || !shouldShowAds()) return;

    let attempts = 0;
    const maxAttempts = 20; // ~5s total
    const interval = 250;

    const tryPush = () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const w = window as any;
        if (typeof w.adsbygoogle !== 'undefined') {
          (w.adsbygoogle = w.adsbygoogle || []).push({});
          return true;
        }
      } catch {}
      return false;
    };

    if (tryPush()) return;

    const timer = window.setInterval(() => {
      attempts += 1;
      if (tryPush() || attempts >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, interval);

    return () => {
      // nothing to cleanup for ads push
    };
  }, []);

  // Only render ads in blog section and not excluded
  if (!shouldShowAds() || shouldExcludeAds()) {
    return null;
  }

  return (
    <div className={`ad-container hidden ${className}`} style={{ textAlign: 'center', margin: '20px 0' }}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={adStyle}
        data-ad-client={AD_CONFIG.clientId}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive="true"
      />
    </div>
  );
};

// Predefined ad components for common placements
export const BlogAdBanner: React.FC = () => (
  <AdComponent 
    adSlot={getAdSlot('blogBanner')}
    adFormat={AD_CONFIG.formats.banner as any}
    className="my-8"
  />
);

export const BlogAdSidebar: React.FC = () => (
  <AdComponent 
    adSlot={getAdSlot('blogSidebar')}
    adFormat={AD_CONFIG.formats.sidebar as any}
    className="my-4"
  />
);

export const BlogAdInContent: React.FC = () => (
  <AdComponent 
    adSlot={getAdSlot('blogInContent')}
    adFormat={AD_CONFIG.formats.inContent as any}
    className="my-6"
  />
);
